import type {
  CampaignListItem,
  CampaignListItemServer,
  CampaignsFilter,
} from '@metapic-apps/shared'

import { normalizeMoney } from './common.adapter'

import { normalizeImage } from '@/lib/adapters/image.adapter'
import { normalizeSharedBudget } from '@/lib/adapters/shared-budgets.adapter'
import { normalizeTrafficSourcesCosts } from '@/lib/adapters/traffic-source-costs.adapter'
import { formatDateToUtcWithoutMilliseconds } from '@/lib/helpers/date.helpers'
import { makeQueryString } from '@/lib/helpers/query.helpers'

const campaignIncludes = ['trafficSourcesCosts', 'store', 'sharedBudget']

export const normalizeCampaignListItem = (data: CampaignListItemServer): CampaignListItem => {
  return {
    id: data.id,
    type: data.type,
    status: data.status,
    internalName: data.name,
    advertiserName: data.store_name,
    ...(data.store_logo && { advertiserLogo: normalizeImage(data.store_logo) }),
    advertiserId: data.store_id,
    advertiserCurrency: data.currency,
    ...(data.traffic_sources_costs && {
      trafficSourceCosts: normalizeTrafficSourcesCosts(data.traffic_sources_costs, data.currency),
    }),
    currentClicks: data.constraints?.current_clicks,
    maxClicks: data.constraints?.max_clicks,
    currentMoney: data.constraints?.current_money,
    maxMoney: data.constraints?.max_money,
    validFrom: data.constraints?.valid_from,
    validUntil: data.constraints?.valid_until,
    ...(data.image && { image: normalizeImage(data.image) }),
    ...(data.targets && {
      targets: {
        users: data.targets.user_ids,
        storeGroups: data.targets.store_group_ids,
        userTags: data.targets.user_tag_ids,
        clients: data.targets.client_ids,
        revenueTiers: data.targets.revenue_tier_ids,
        emails: data.targets.emails,
        socialMediaIdentifiers: data.targets.social_media_identifiers,
      },
    }),
    perUserLimit: Boolean(data.constraints?.per_user_limit),
    ...(data.consumed_budget && {
      consumedBudget: {
        amount: Number(data.consumed_budget.amount),
        currency: data.consumed_budget.currency,
      },
    }),
    ...(data.budget_limit_v2 && { budgetLimit: normalizeMoney(data.budget_limit_v2) }),
    ...(data.shared_budget && { sharedBudget: normalizeSharedBudget(data.shared_budget) }),
  }
}

export const handleFilter = (filter: CampaignsFilter, id: number): CampaignsFilter => {
  const result = { ...filter }

  if (result.createdBefore) {
    result.createdBefore = formatDateToUtcWithoutMilliseconds(result.createdBefore)
  }

  delete result.createdByMe

  if (filter.createdByMe) {
    result.createdBy = id
  }

  if (filter.sortBy === 'ends_soon') {
    delete result.sortBy
    result.customSortBy = 'ends_soon'
  }

  return result
}

export const makeQueryStringForCampaigns = (filter: CampaignsFilter, id: number): string | null => {
  const updatedFilter = handleFilter(filter, id)

  return makeQueryString(updatedFilter)
}

export const makeQueryParamsForCampaigns = (
  filter: CampaignsFilter,
  id: number,
): URLSearchParams | undefined => {
  const queryString = makeQueryStringForCampaigns(filter, id)

  if (!queryString) {
    return undefined
  }

  const params = new URLSearchParams(queryString)

  campaignIncludes.forEach((include) => {
    params.append('include[]', include)
  })

  return params
}
